import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLevelAccess } from '../../context/LevelAccessContext';
import { apiGetDashboard } from '../../utils/api';
import { Card, LevelBadge, Button, ProgressBar, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const CEFR_LEVELS = ['A1','A2','B1','B2'];
const levelMeta = {
  A1:{ title:'Beginner',           desc:'Everyday expressions and basic phrases' },
  A2:{ title:'Elementary',         desc:'Simple conversations on familiar topics' },
  B1:{ title:'Intermediate',       desc:'Main points on familiar matters' },
  B2:{ title:'Upper Intermediate', desc:'Complex texts and abstract topics' },
};
// Quick-access cards using brand palette
const navCards = [
  { to:'/student/curriculum', icon:'📚', title:'Curriculum',  desc:'Your learning roadmap',    color:'bg-surface border-gray-200', iconBg:'bg-de-black text-white' },
  { to:'/student/results',    icon:'📊', title:'My Results',  desc:'Scores and AI feedback',   color:'bg-surface border-gray-200', iconBg:'bg-green-500 text-white' },
];
const activityIcons = { exam:'📝', achievement:'🏆' };

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60)   return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m/60)} hr ago`;
  return `${Math.floor(m/1440)} days ago`;
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const { getLevelStatus, currentLevel, completedLevels, unlockedLevels } = useLevelAccess();
  const navigate = useNavigate();
  const [dashData, setDashData]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [cohort, setCohort]       = useState(null);
  const [cohortLoading, setCohortLoading] = useState(true);
  const [hasPaidNoCohort, setHasPaidNoCohort] = useState(false);

  useEffect(() => {
    apiGetDashboard()
      .then(setDashData)
      .catch(() => {})
      .finally(() => setLoading(false));

    // Check cohort enrollment
    API.get('/cohorts/my-cohort')
      .then(({ data }) => {
        const myCohort = data.cohorts?.[0] || null;
        setCohort(myCohort);
        // If no cohort yet, check if they have a paid payment
        if (!myCohort) {
          API.get('/payments/my-payments')
            .then(({ data: pd }) => {
              const paid = (pd.payments || []).some(p => p.status === 'paid');
              setHasPaidNoCohort(paid);
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setCohortLoading(false));
  }, []);

  const recentActivity = dashData?.recentActivity || [];
  const weakPoints     = dashData?.weakPoints     || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome banner — black bg with red and gold accents */}
      <div className="rounded-2xl p-7 mb-6 text-white relative overflow-hidden" style={{ background:'#1A1A1A' }}>
        {/* Gold top stripe */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gold-400" />
        <div className="absolute right-0 top-0 bottom-0 w-64 opacity-5 flex items-center justify-center pointer-events-none">
          <span className="font-display text-[120px] font-bold select-none">D</span>
        </div>
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <p className="text-gray-400 text-sm mb-1">Willkommen zurück,</p>
            <h1 className="font-display text-3xl font-bold text-white mb-2">{user?.name || 'Student'}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-500 rounded-full text-sm font-semibold">
                ⭐ Level {currentLevel} — Active
              </span>
              {user?.student?.student_number && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-sm font-mono text-gold-400">
                  🆔 {user.student.student_number}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gold-400/20 border border-gold-400/30 rounded-full text-sm text-gold-300">
                ✓ Enrolled
              </span>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="rounded-xl p-4 text-center border border-white/10" style={{ background:'rgba(255,255,255,0.05)' }}>
              <p className="text-2xl font-bold text-gold-400">{completedLevels.length}</p>
              <p className="text-xs text-gray-400">Levels done</p>
            </div>
            <div className="rounded-xl p-4 text-center border border-white/10" style={{ background:'rgba(255,255,255,0.05)' }}>
              <p className="text-2xl font-bold text-white">{unlockedLevels.length}</p>
              <p className="text-xs text-gray-400">Unlocked</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Choose cohort banner — shown when paid but no cohort yet ── */}
      {!cohortLoading && hasPaidNoCohort && !cohort && (
        <div className="mb-6 rounded-2xl border-2 border-gold-400 bg-gold-50 p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏫</span>
            <div>
              <p className="text-sm font-bold text-de-black">Choose your class time!</p>
              <p className="text-xs text-gray-600">Your payment is confirmed — select a cohort to start your German journey.</p>
            </div>
          </div>
          <Link to="/student/choose-cohort"
            className="flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-colors hover:opacity-90"
            style={{ background:'#DD0000' }}>
            Choose Now →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* My Levels */}
          <Card className="p-6" accent="red">
            <SectionHeader title="My Levels" subtitle="Track your progress across all CEFR levels" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CEFR_LEVELS.map(level => {
                const status = getLevelStatus(level);
                const meta   = levelMeta[level];
                return (
                  <div key={level} className={`rounded-xl border-2 p-4 transition-all
                    ${status === 'completed' ? 'border-green-300 bg-green-50'  : ''}
                    ${status === 'active'    ? 'border-gold-400 bg-gold-50'    : ''}
                    ${status === 'locked'    ? 'border-gray-200 bg-surface opacity-70' : ''}
                  `}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-lg text-de-black">{level}</span>
                          <LevelBadge level={level} status={status} />
                        </div>
                        <p className="text-xs font-semibold text-de-text">{meta.title}</p>
                        <p className="text-xs text-gray-500">{meta.desc}</p>
                      </div>
                      {status === 'completed' && <span className="text-2xl">✅</span>}
                      {status === 'active'    && <span className="text-2xl">⭐</span>}
                      {status === 'locked'    && <span className="text-2xl">🔒</span>}
                    </div>
                    <div className="mt-3">
                      {status === 'completed' && (
                        <Button variant="secondary" size="sm" onClick={() => navigate('/student/curriculum')}>Review</Button>
                      )}
                      {status === 'active' && (
                        <Button variant="primary" size="sm" onClick={() => navigate('/student/curriculum')}>Continue →</Button>
                      )}
                      {status === 'locked' && (
                        <Button variant="ghost" size="sm" onClick={() => navigate('/register')}>🔒 Register &amp; Pay</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Quick navigation */}
          <Card className="p-6">
            <SectionHeader title="Quick Access" />
            <div className="grid grid-cols-2 gap-3">
              {navCards.map(card => (
                <Link key={card.title} to={card.to}>
                  <div className={`rounded-xl border p-4 hover:shadow-md hover:border-brand-300 transition-all cursor-pointer bg-white ${card.color}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl mb-3 ${card.iconBg}`}>
                      {card.icon}
                    </div>
                    <p className="font-semibold text-sm text-de-black">{card.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{card.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <Card className="p-6">
            <SectionHeader title="Recent Activity" subtitle="Your latest sessions" />
            {loading ? (
              <div className="flex justify-center py-6"><Spinner className="text-brand-500" /></div>
            ) : recentActivity.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No activity yet — start learning!</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-surface flex items-center justify-center text-base flex-shrink-0 border border-gray-200">
                      {activityIcons[item.type] || '📌'}
                    </div>
                    <div>
                      <p className="text-xs text-de-text leading-snug">{item.text}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(item.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* My Cohort */}
          {cohort && (
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-de-black text-white flex items-center justify-center text-sm">🏫</div>
                <div>
                  <h3 className="text-sm font-bold text-de-black">My Cohort</h3>
                  <p className="text-xs text-gray-500 capitalize">{cohort.session} session</p>
                </div>
              </div>
              <p className="text-sm font-semibold text-de-black mb-2">{cohort.name}</p>
              <div className="space-y-1 text-xs text-gray-500 mb-3">
                <p>📅 {Array.isArray(cohort.days) ? cohort.days.map(d => d.slice(0,3)).join(', ') : cohort.days}</p>
                <p>⏰ {cohort.start_time} – {cohort.end_time}</p>
                {cohort.start_date && (() => {
                  const start     = new Date(cohort.start_date);
                  const now       = new Date();
                  const weekNum   = Math.max(1, Math.ceil((now - start) / (7 * 24 * 60 * 60 * 1000)));
                  const totalWeeks = 10;
                  const pct       = Math.min(100, Math.round((weekNum / totalWeeks) * 100));
                  return (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Week {Math.min(weekNum, totalWeeks)} of {totalWeeks}</span>
                        <span>{pct}% complete</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-brand-500" style={{ width:`${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}
              </div>
              {cohort.meet_link && (
                <a href={cohort.meet_link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-white text-xs font-bold hover:opacity-90 transition-opacity"
                  style={{ background:'#DD0000' }}>
                  📹 Join Class on Google Meet
                </a>
              )}
              <Link to="/student/timetable" className="block text-center text-xs text-brand-500 hover:text-brand-700 mt-2 font-semibold">
                View Full Timetable →
              </Link>
            </Card>
          )}

          {/* AI Weak Points */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-brand-500 text-white flex items-center justify-center text-sm">🤖</div>
              <div>
                <h3 className="text-sm font-bold text-de-black">AI Weak Points</h3>
                <p className="text-xs text-gray-500">Areas needing attention</p>
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-4"><Spinner size="sm" className="text-brand-500" /></div>
            ) : weakPoints.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No weak points flagged yet.</p>
            ) : (
              <div className="space-y-2">
                {weakPoints.slice(0, 4).map((wp, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-surface">
                    <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0
                      ${wp.severity === 'high' ? 'bg-brand-500' : wp.severity === 'medium' ? 'bg-gold-400' : 'bg-gray-400'}`} />
                    <div>
                      <p className="text-xs text-de-text">{wp.description}</p>
                      <p className="text-xs text-gray-400 capitalize">{wp.type} · Seen {wp.frequency}×</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/student/results" className="text-xs text-brand-500 hover:text-brand-700 mt-3 font-semibold block">
              View full analysis →
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}
